/**
 * Recurring calendar-event types — mirrors the Laravel contract exactly
 * (app/Enum/RecurringFrequency, RecurringMonthlyMode, Weekday, RecurringEditScope
 * and StoreRecurringRequest in conversional-laravel). A series is created once via
 * `POST /calendar-events/recurring`; there is no endpoint to change its pattern
 * afterwards — only title/description/color/time propagate to siblings via
 * `apply_to` on the single-event endpoints.
 */

/** Matches App\Enum\RecurringFrequency. No YEARLY — the backend doesn't support it. */
export enum RecurrenceFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

/** Matches App\Enum\RecurringMonthlyMode. */
export enum RecurrenceMonthlyMode {
  DAY_OF_MONTH = 'day_of_month',
  WEEKDAY = 'weekday',
}

/** Matches App\Enum\Weekday — full lowercase names, not RRULE two-letter tokens. */
export type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/** Monday-first, matching Weekday::getAll(). */
export const WEEKDAYS: Weekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** How a recurrence terminates. UI-only concept — serializes to `count` / `until` (never both, never neither in ON_DATE/AFTER_OCCURRENCES). */
export enum RecurrenceEndType {
  NEVER = 'never',
  ON_DATE = 'on_date',
  AFTER_OCCURRENCES = 'after',
}

export interface RecurrenceEnd {
  type: RecurrenceEndType;
  /** ISO date string, used when type === ON_DATE. Sent to the API as `until`. */
  until?: string;
  /** Number of occurrences, used when type === AFTER_OCCURRENCES. Sent to the API as `count`. */
  count?: number;
}

/**
 * UI-side representation of a recurrence rule. Always anchored to the event's
 * start date — the monthly day-of-month / nth-weekday and the weekly default
 * weekday are derived from the anchor rather than stored here, so the rule
 * stays correct if the start date changes while the editor is open. See
 * {@link toRecurringPayload} for the anchor → `StoreRecurringRequest` mapping.
 */
export interface RecurrenceConfig {
  frequency: RecurrenceFrequency;
  /** Repeat every N days/weeks/months. Max RECURRENCE_MAX_INTERVAL. */
  interval: number;
  /** Weekly: the selected weekdays. */
  byWeekdays?: Weekday[];
  /** Monthly: repeat on a fixed day-of-month vs. an ordinal weekday (both derived from the anchor). */
  monthlyMode?: RecurrenceMonthlyMode;
  /**
   * Monthly day-of-month mode: an explicit day (1-31) chosen in the editor.
   * Unset means "follow the anchor's day of month".
   */
  dayOfMonth?: number;
  end: RecurrenceEnd;
}

/** Matches RecurringFrequency::MAX_INTERVAL / MAX_OCCURRENCES (conversional-laravel). */
export const RECURRENCE_MAX_INTERVAL = 52;
export const RECURRENCE_MAX_OCCURRENCES = 366;

/**
 * A persisted series as the backend returns it (RecurringResource, embedded as
 * `recurring` on every occurrence's CalendarEventResource). The pattern fields
 * are write-once. `start_at`/`end_at` are the FIRST occurrence's UTC start/end —
 * the series' canonical time-of-day and duration. Monthly weekday-mode stores
 * its weekday in `by_weekdays[0]` (see RecurringService in conversional-laravel).
 */
export interface RecurringModel {
  /** Uuid of the series (Recurring#uuid — not a Hashids id). */
  id: string;
  frequency: RecurrenceFrequency;
  interval: number;
  by_weekdays: Weekday[] | null;
  monthly_mode: RecurrenceMonthlyMode | null;
  day_of_month: number | null;
  /** 1-5, or -1 for the last week of the month. */
  week_ordinal: number | null;
  start_at: string | null;
  end_at: string | null;
  until_at: string | null;
  count: number | null;
  /** IANA name, e.g. 'Europe/Berlin'. */
  timezone: string | null;
  created?: string;
}

/**
 * Scope of an edit/delete applied to a recurring series — the classic
 * "only this / this and following / all" prompt. Matches App\Enum\RecurringEditScope.
 * Sent to the backend as `apply_to`: JSON body field on PUT/PATCH, query
 * parameter on DELETE.
 */
export enum RecurrenceScope {
  ONLY_THIS = 'only_this',
  THIS_AND_FOLLOWING = 'this_and_following',
  ALL = 'all',
}
